import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Scale, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { borrarSombra, getSombra, getSombras, type EjercicioSombraItem } from "@/lib/api";
import { usePermisos } from "@/lib/authz";
import { ResumenFactura } from "@/pages/FacturasPage";

const mxn = (n: number, moneda = "MXN") =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: moneda === "USD" ? "USD" : "MXN" }).format(n);

const num = (n: number, d = 2) =>
  new Intl.NumberFormat("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);

const colorDif = (dif: number) =>
  Math.abs(dif) < 0.5 ? "text-emerald-700" : dif > 0 ? "text-red-700" : "text-amber-700";

// ---------- Lista de ejercicios sombra (/sombra/guardados) -------------------

// Columnas ordenables de la lista (clic en el encabezado alterna asc/desc).
const SORT_VAL: Record<string, (it: EjercicioSombraItem) => string | number> = {
  id: (it) => it.id,
  contrato: (it) => it.folio_contrato,
  contraparte: (it) => it.contraparte,
  producto: (it) => it.producto ?? "Energía",
  periodo: (it) => it.desde,
  subtotal: (it) => it.subtotal,
  iva: (it) => it.iva,
  modelo: (it) => it.total,
  factura: (it) => it.fac_total,
  dif: (it) => it.dif_total,
};

export function SombrasGuardadasPage() {
  const qc = useQueryClient();
  const { has } = usePermisos();
  const puedeBorrar = has("borrar_sombra");
  const q = useQuery({ queryKey: ["sombras"], queryFn: getSombras });
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState("id");
  const [sortDir, setSortDir] = useState<1 | -1>(-1); // default: más recientes arriba

  // Filtros: por contrato y por periodo (Año-Mes). El mes filtra por TRASLAPE:
  // entra todo ejercicio cuyo periodo TOCA ese mes (una quincena que cruza de
  // mes aparece en ambos).
  const [fContrato, setFContrato] = useState("");
  const [fMes, setFMes] = useState("");
  const todos = q.data ?? [];
  const contratosDisp = Array.from(new Set(todos.map((e) => e.folio_contrato))).sort();
  const finMes = (ym: string) => {
    const [a, m] = ym.split("-").map(Number);
    const d = new Date(a, m, 0); // día 0 del mes siguiente = último del mes
    return `${ym}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const filtrados = todos.filter(
    (e) =>
      (!fContrato || e.folio_contrato === fContrato) &&
      (!fMes || (e.desde <= finMes(fMes) && e.hasta >= `${fMes}-01`)),
  );

  const toggleSort = (k: string) => {
    if (sortKey === k) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(k);
      setSortDir(1);
    }
  };
  const items = [...filtrados].sort((a, b) => {
    const va = SORT_VAL[sortKey]?.(a) ?? 0;
    const vb = SORT_VAL[sortKey]?.(b) ?? 0;
    const cmp =
      typeof va === "number" && typeof vb === "number"
        ? va - vb
        : String(va).localeCompare(String(vb), "es");
    return cmp * sortDir;
  });

  const mBorrar = useMutation({
    mutationFn: (id: number) => borrarSombra(id),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["sombras"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Error al borrar el ejercicio"),
  });

  const Th = ({ k, children, right }: { k: string; children: React.ReactNode; right?: boolean }) => (
    <th
      onClick={() => toggleSort(k)}
      title="Ordenar por esta columna"
      className={`px-4 py-2 cursor-pointer select-none hover:text-slate-800 ${right ? "text-right" : "text-left"}`}
    >
      {children}
      {sortKey === k && <span className="ml-1">{sortDir === 1 ? "▲" : "▼"}</span>}
    </th>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Ejercicios sombra guardados</h1>
          <p className="text-sm text-slate-500 mt-1">
            Validaciones de compra guardadas: modelo vs factura recibida, con la diferencia.
          </p>
        </div>
        <Link to="/sombra" className="text-blue-600 hover:underline text-sm">
          + Nuevo ejercicio
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}

      {/* Filtros: contrato + mes (por traslape de periodo), igual que /facturas */}
      <div className="bg-white border rounded-lg px-4 py-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Contrato</span>
          <select
            className="mt-1 border rounded px-2 py-1.5 text-sm w-56"
            value={fContrato}
            onChange={(e) => setFContrato(e.target.value)}
          >
            <option value="">Todos</option>
            {contratosDisp.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Periodo (Año-Mes)</span>
          <input
            type="month"
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={fMes}
            onChange={(e) => setFMes(e.target.value)}
            title="Muestra los ejercicios cuyo periodo toca este mes"
          />
        </label>
        {(fContrato || fMes) && (
          <button
            type="button"
            onClick={() => {
              setFContrato("");
              setFMes("");
            }}
            className="text-sm text-slate-500 hover:text-slate-800 underline pb-2"
          >
            Limpiar filtros
          </button>
        )}
        <span className="text-[11px] text-slate-400 pb-2 ml-auto">
          {filtrados.length} de {todos.length} ejercicio{todos.length === 1 ? "" : "s"}
          {fMes && " · el mes filtra por traslape del periodo"}
        </span>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        {q.isLoading ? (
          <div className="p-4 text-sm text-slate-500">Cargando…</div>
        ) : todos.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">
            Aún no hay ejercicios guardados. Genera uno en <b>Ejercicio Sombra</b> y presiona
            “Guardar ejercicio”.
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">
            Ningún ejercicio {fContrato && <>de <b>{fContrato}</b> </>}
            {fMes && <>toca el mes <b>{fMes}</b></>}. Ajusta o limpia los filtros.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 border-b">
                <Th k="id">#</Th>
                <Th k="contrato">Contrato</Th>
                <Th k="contraparte">Contraparte</Th>
                <Th k="producto">Producto</Th>
                <Th k="periodo">Periodo</Th>
                <Th k="subtotal" right>Sin IVA</Th>
                <Th k="iva" right>IVA</Th>
                <Th k="modelo" right>Modelo (total)</Th>
                <Th k="factura" right>Factura</Th>
                <Th k="dif" right>Diferencia</Th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-500">{it.id}</td>
                  <td className="px-4 py-2 font-medium">{it.folio_contrato}</td>
                  <td className="px-4 py-2">{it.contraparte}</td>
                  <td className="px-4 py-2">
                    <span className="text-[11px] font-medium bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                      {it.producto ?? "Energía"}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {it.desde} a {it.hasta}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{mxn(it.subtotal, it.moneda)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{mxn(it.iva, it.moneda)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{mxn(it.total, it.moneda)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{mxn(it.fac_total, it.moneda)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums font-medium ${colorDif(it.dif_total)}`}>
                    {it.dif_total > 0 ? "+" : ""}
                    {mxn(it.dif_total, it.moneda)}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <Link to={`/sombra/guardados/${it.id}`} className="text-blue-600 hover:underline">
                      Abrir →
                    </Link>
                    {puedeBorrar && (
                      <button
                        type="button"
                        title="Borrar ejercicio (libera los días del periodo)"
                        disabled={mBorrar.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `¿Borrar el ejercicio #${it.id} de ${it.folio_contrato} ` +
                                `(${it.desde} a ${it.hasta})? Los días del periodo quedan ` +
                                "libres para rehacer el ejercicio.",
                            )
                          ) {
                            mBorrar.mutate(it.id);
                          }
                        }}
                        className="ml-3 text-slate-400 hover:text-red-600 disabled:opacity-40 align-middle"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------- Detalle de un ejercicio sombra (/sombra/guardados/:id) -----------

export function SombraGuardadaDetallePage() {
  const { id } = useParams<{ id: string }>();
  const ejId = Number(id);
  const q = useQuery({
    queryKey: ["sombra", ejId],
    queryFn: () => getSombra(ejId),
    enabled: Number.isFinite(ejId),
  });

  const e = q.data;

  return (
    <div className="space-y-5">
      <Link to="/sombra/guardados" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm">
        <ArrowLeft className="w-4 h-4" /> Ejercicios guardados
      </Link>

      {q.isLoading && <div className="text-sm text-slate-500">Cargando…</div>}
      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          No se pudo cargar el ejercicio.
        </div>
      )}

      {e && (
        <>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              Ejercicio sombra #{e.id} — {e.folio_contrato}
              <span className="ml-2 align-middle text-[11px] font-medium bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                {e.producto ?? "Energía"}
              </span>
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {e.contraparte} · {e.desde} a {e.hasta} · guardado{" "}
              {new Date(e.guardado_en).toLocaleString("es-MX")}
              {e.guardado_por ? ` por ${e.guardado_por}` : ""}
            </p>
          </div>

          {/* Comparativo guardado (solo lectura) */}
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
              <Scale className="w-4 h-4 text-slate-500" />
              <span className="font-medium text-slate-800">Factura recibida vs modelo</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 border-b">
                  <th className="px-4 py-2 text-left">Concepto</th>
                  <th className="px-4 py-2 text-right">Factura recibida</th>
                  <th className="px-4 py-2 text-right">Modelo</th>
                  <th className="px-4 py-2 text-right">Diferencia</th>
                </tr>
              </thead>
              <tbody>
                {/* Potencia y CEL tienen un solo concepto (guardado en ce_q) */}
                {e.resultado_potencia || e.resultado_cel ? (
                  <FilaRO
                    etiqueta={
                      (e.resultado_potencia ?? e.resultado_cel)!.descripcion ?? "Importe"
                    }
                    fac={e.fac_ce_q}
                    mod={e.ce_q}
                    moneda={e.moneda}
                  />
                ) : (
                  <>
                    <FilaRO etiqueta="Cargo por Energía (CE_q)" fac={e.fac_ce_q} mod={e.ce_q} moneda={e.moneda} />
                    <FilaRO etiqueta="Tarifas Reguladas (CTreg_q)" fac={e.fac_ctreg_q} mod={e.ctreg_q} moneda={e.moneda} />
                    {e.moneda !== "USD" && (
                      <FilaRO etiqueta="Cargo Fijo Quincenal (CF_q)" fac={e.fac_cf_q} mod={e.cf_q} moneda={e.moneda} />
                    )}
                  </>
                )}
                <FilaRO etiqueta="Subtotal (sin IVA)" fac={e.fac_subtotal} mod={e.subtotal} bold moneda={e.moneda} />
                <FilaRO etiqueta="IVA" fac={e.fac_iva} mod={e.iva} moneda={e.moneda} />
                <FilaRO etiqueta="TOTAL a pagar" fac={e.fac_total} mod={e.total} bold total moneda={e.moneda} />
              </tbody>
            </table>
          </div>

          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-1">
            Soporte del modelo
          </div>
          {e.resultado && <ResumenFactura res={e.resultado} />}
          {(e.resultado_potencia || e.resultado_cel) && (
            <div className="bg-white border rounded-lg p-4 text-sm space-y-1">
              {e.resultado_potencia && (
                <>
                  <div>
                    <b>{num(e.resultado_potencia.cantidad, 4)}</b> MAW ×{" "}
                    <b>{mxn(e.resultado_potencia.precio_unitario, e.moneda)}</b> ={" "}
                    <b>{mxn(e.resultado_potencia.importe, e.moneda)}</b>
                  </div>
                  <div className="text-slate-500">
                    Pago {e.resultado_potencia.n_pago} de{" "}
                    {e.resultado_potencia.num_parcialidades} · {e.resultado_potencia.pc_mw_ano}{" "}
                    MW-año contratados · acumulado{" "}
                    {mxn(e.resultado_potencia.acumulado_importe, e.moneda)}
                  </div>
                </>
              )}
              {e.resultado_cel && (
                <div>
                  <b>{num(e.resultado_cel.cel, 0)}</b> CELs ×{" "}
                  <b>{mxn(e.resultado_cel.precio_unitario, e.moneda)}</b> ={" "}
                  <b>{mxn(e.resultado_cel.importe, e.moneda)}</b>
                </div>
              )}
              {(e.resultado_potencia ?? e.resultado_cel)!.advertencias.map((a, i) => (
                <div key={i} className="text-xs text-amber-700">⚠ {a}</div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FilaRO({
  etiqueta,
  fac,
  mod,
  bold,
  total,
  moneda = "MXN",
}: {
  etiqueta: string;
  fac: number;
  mod: number;
  bold?: boolean;
  total?: boolean;
  moneda?: string;
}) {
  const dif = fac - mod;
  const coincide = Math.abs(dif) < 0.5;
  return (
    <tr className={`border-t ${total ? "border-t-2 border-slate-300 bg-emerald-50" : bold ? "bg-slate-50" : ""}`}>
      <td className={`px-4 py-2 ${bold ? "font-medium" : ""}`}>{etiqueta}</td>
      <td className={`px-4 py-2 text-right tabular-nums ${bold ? "font-medium" : ""}`}>{mxn(fac, moneda)}</td>
      <td className={`px-4 py-2 text-right tabular-nums ${bold ? "font-medium" : ""}`}>{mxn(mod, moneda)}</td>
      <td className={`px-4 py-2 text-right tabular-nums font-medium ${colorDif(dif)}`}>
        {coincide ? "≈ " : dif > 0 ? "+" : ""}
        {mxn(dif, moneda)}
      </td>
    </tr>
  );
}
